import * as yup from "yup";
import { RFC_EMAIL_SPLITTER_REGEX } from "~/components/InvoiceObject/utils";
import { validateCompanyName } from "~/utils/companyCheckValidator";
import { countriesWithRequiredRegion } from "~/utils/utils";

export const estimateValidator = (company, skipCompanyValidation) => {
  return yup.object().shape({
    name: yup.string().required(),
    company_name: yup.string().when("shouldValidate", {
      is: () => skipCompanyValidation,
      then: yup.string().notRequired(),
      otherwise: yup
        .string()
        .required()
        .test("company-validation", "Company validation failed", async function (value) {
          const validation = await validateCompanyName({ ...company, name: value });
          if (!validation.isValid) {
            return this.createError({ message: validation.error });
          }
          return true;
        }),
    }),
    company_id: yup.number().nullable(),
    company_address: yup.string(),
    currency_id: yup.number().required(),
    language_id: yup.number().required(),
    items: yup
      .array()
      .of(
        yup.object().shape({
          quantity: yup.number().required().min(0.01).max(9999999999.99),
          unit_cost: yup.number().required().max(999999999999.999),
          subtotal: yup.number().required().max(999999999999.999),
        })
      )
      .required(),
    issued_on: yup.date().required(),
    discount_rate: yup.number().min(0).max(100),
    address_line_1: yup.string().when("shouldValidate", {
      is: () => skipCompanyValidation,
      then: yup.string().notRequired(),
      otherwise: yup.string().trim().required(),
    }),
    address_line_2: yup.string().trim(),
    city: yup.string().when("shouldValidate", {
      is: () => skipCompanyValidation,
      then: yup.string().notRequired(),
      otherwise: yup.string().trim().required(),
    }),
    zip_code: yup.string().when("shouldValidate", {
      is: () => skipCompanyValidation,
      then: yup.string().notRequired(),
      otherwise: yup.string().trim().required(),
    }),
    region: yup
      .string()
      .trim()
      .when("country", {
        is: (value) => countriesWithRequiredRegion.includes(value),
        then: yup.string().trim().required(),
        otherwise: yup.string().trim(),
      }),
    country: yup.string().when("shouldValidate", {
      is: () => skipCompanyValidation,
      then: yup.string().notRequired(),
      otherwise: yup.string().trim().required(),
    }),
    tax_number: yup.string().trim(),
  });
};

export let validationAddressForEstimatesWhenNotRequired = yup.object().shape({
  address_line_1: yup.string().trim(),
  address_line_2: yup.string().trim(),
  city: yup.string().trim(),
  zip_code: yup.string().trim(),
  region: yup
    .string()
    .trim()
    .when("country", {
      is: (value) => {
        return countriesWithRequiredRegion.includes(value);
      },
      then: yup.string().trim(),
      otherwise: yup.string().trim(),
    }),
  country: yup.string().trim(),
  tax_number: yup.string().trim(),
});

let validateRestOfFormForEstimates = (company, skipCompanyValidation) => {
  return yup.object().shape({
    name: yup.string().required(),
    company_name: yup.string().when("shouldValidate", {
      is: () => skipCompanyValidation,
      then: yup.string().notRequired(),
      otherwise: yup
        .string()
        .required()
        .test("company-validation", "Company validation failed", async function (value) {
          const validation = await validateCompanyName({ ...company, name: value });
          if (!validation.isValid) {
            return this.createError({ message: validation.error });
          }
          return true;
        }),
    }),
    company_id: yup.number().nullable(),
    company_address: yup.string(),
    currency_id: yup.number().required(),
    language_id: yup.number().required(),
    items: yup
      .array()
      .of(
        yup.object().shape({
          quantity: yup.number().min(0.01).max(9999999999.99),
          unit_cost: yup.number().max(999999999999.999),
          subtotal: yup.number().max(999999999999.999),
        })
      )
      .required(),
    issued_on: yup.date().required(),
    discount_rate: yup.number().min(0).max(100),
  });
};

export const validateEstimatesWhenAddressNotRequired = (company, skipCompanyValidation) => {
  return validationAddressForEstimatesWhenNotRequired
    .pick(["address_line_1", "address_line_2", "city", "zip_code", "region", "country", "tax_number"])
    .concat(validateRestOfFormForEstimates(company, skipCompanyValidation));
};

export const sendEstimateValidator = yup.object().shape({
  recipientsRequired: yup.boolean(),
  recipients: yup.string().test("are-recipients-valid", "Email addresses are invalid", function (value) {
    const { path, createError } = this;
    const { recipientsRequired } = this.parent;

    if (!value) {
      if (!recipientsRequired) {
        return true;
      } else {
        return createError({ path, message: "At-least one email address is required" });
      }
    }

    const emailAddresses = value.split(",")?.map((v) => {
      if (!v.match(RFC_EMAIL_SPLITTER_REGEX)) {
        return v;
      }
      return v.match(RFC_EMAIL_SPLITTER_REGEX);
    });

    if (!emailAddresses?.length) {
      return createError({ path, message: "At-least one email address is required" });
    }

    return true;
  }),
});
